/**
 * Reading an access token's expiry, without a dependency on anything that reads one.
 *
 * Its own module because both ends of the session need it and they are on opposite sides of an
 * import: `lib/api/client` refreshes a token that is about to run out, and `features/auth/session`
 * decides whether a visitor is signed in at all. A shared pure function means neither has to
 * import the other.
 */

/**
 * Read the `exp` claim without verifying the signature. This is only a hint that saves a
 * doomed request; the API is the authority on whether a token is still good.
 */
export function tokenExpiresAt(token: string): number | null {
  const payload = token.split('.')[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const claims: unknown = JSON.parse(atob(padded))
    if (
      typeof claims === 'object' &&
      claims !== null &&
      'exp' in claims &&
      typeof claims.exp === 'number'
    ) {
      return claims.exp * 1000
    }
    return null
  } catch {
    return null
  }
}

/**
 * Is this token too close to its expiry to be worth sending?
 *
 * The margin covers the flight time of the request it is about to be attached to, plus whatever
 * the phone's clock is out by. A token with no readable `exp` is not treated as expiring: we
 * cannot tell, and the API is the one that decides anyway.
 */
export function expiresWithin(token: string, marginMs: number, now = Date.now()): boolean {
  const expiresAt = tokenExpiresAt(token)
  return expiresAt !== null && expiresAt - marginMs <= now
}
